import { useState, useEffect } from "react";
import { toast } from "react-toastify";
import { MdClose } from "react-icons/md";
import tw from "tailwind-styled-components";
import { ActionSection, SideCancelBtn, SubmitBtn } from "../../Styles/InputStyles";
import { foodItemCategories } from "../../../Config";

const FilterModal = ({
    onApplyFilter,
    handleClose,
    currentFilter = null,
    handleClearFilters,
    kitchenList = []
}) => {
    const [activeType, setActiveType] = useState('categories');
    const [selectedFilters, setSelectedFilters] = useState({
        categories: [],
        kitchens: []
    });
    const [height, setHeight] = useState(window.innerHeight);

    // Filter groups - Categories and Kitchens
    const filterGroups = [
        {
            key: "categories",
            title: "Categories",
            options: foodItemCategories.map(item => ({
                label: item.label,
                value: String(item.value),
            })),
            multiSelect: true
        },
        {
            key: "kitchens",
            title: "Kitchens",
            options: kitchenList.map(item => ({
                label: item.name,
                value: String(item.id),
            })),
            multiSelect: true
        },
    ];

    // Preselect on open
    useEffect(() => {
        if (!currentFilter) return;

        const newSelectedFilters = {
            categories: [],
            kitchens: []
        };

        // If currentFilter is an object with categories and/or kitchens
        if (typeof currentFilter === 'object') {
            if (currentFilter.categories && Array.isArray(currentFilter.categories)) {
                newSelectedFilters.categories = currentFilter.categories.map(item => ({
                    value: String(item.value),
                    label: item.label,
                    type: "Categories"
                }));
            }
            if (currentFilter.kitchens && Array.isArray(currentFilter.kitchens)) {
                newSelectedFilters.kitchens = currentFilter.kitchens.map(item => ({
                    value: String(item.value),
                    label: item.label,
                    type: "Kitchens"
                }));
            }
            setSelectedFilters(newSelectedFilters);

            // Set active type to the first filter that has selections
            if (newSelectedFilters.categories.length > 0) {
                setActiveType('categories');
            } else if (newSelectedFilters.kitchens.length > 0) {
                setActiveType('kitchens');
            }
        }
        // If currentFilter is a string (single category)
        else if (typeof currentFilter === 'string') {
            const match = foodItemCategories.find(cat => String(cat.value) === currentFilter);
            if (match) {
                setSelectedFilters({
                    categories: [{
                        value: String(match.value),
                        label: match.label,
                        type: "Categories"
                    }],
                    kitchens: []
                });
                setActiveType('categories');
            }
        }
    }, [currentFilter]);

    useEffect(() => {
        const resize = () => setHeight(window.innerHeight);
        window.addEventListener("resize", resize);
        return () => window.removeEventListener("resize", resize);
    }, []);

    const handleApply = () => {
        const hasSelectedFilters =
            selectedFilters.categories.length > 0 ||
            selectedFilters.kitchens.length > 0;

        if (!hasSelectedFilters) {
            toast.error("Please select at least one filter before applying.");
            return;
        }

        onApplyFilter(selectedFilters);
    };

    const handleClearAllFilters = () => {
        setSelectedFilters({
            categories: [],
            kitchens: []
        });
        if (handleClearFilters) {
            handleClearFilters();
        }
        setActiveType('categories');
    };

    const toggleFilter = (groupKey, option) => {
        setSelectedFilters(prev => {
            const currentFilters = prev[groupKey] || [];
            const isSelected = currentFilters.some(f => f.value === option.value);
            const group = filterGroups.find(g => g.key === groupKey);

            if (isSelected) {
                return {
                    ...prev,
                    [groupKey]: currentFilters.filter(f => f.value !== option.value)
                };
            } else {
                return {
                    ...prev,
                    [groupKey]: [
                        ...currentFilters,
                        {
                            value: option.value,
                            label: option.label,
                            type: group.title
                        }
                    ]
                };
            }
        });
    };

    const isFilterSelected = (groupKey, optionValue) => {
        return selectedFilters[groupKey]?.some(f => f.value === optionValue) || false;
    };

    const getSelectedCount = (groupKey) => {
        return selectedFilters[groupKey]?.length || 0;
    };

    const activeGroup = filterGroups.find(g => g.key === activeType) || filterGroups[0];

    return (
        <Container style={{ height }}>
            {/* Header */}
            <ModalHeader>
                <h1 className="text-lg font-medium text-white">Filters</h1>
                <button onClick={handleClose}>
                    <MdClose color="white" size={24} />
                </button>
            </ModalHeader>

            {/* Body */}
            <Body>
                {/* LEFT – Filter Types */}
                <LeftPane>
                    {filterGroups.map(group => {
                        const active = activeType === group.key;
                        const count = getSelectedCount(group.key);

                        return (
                            <TypeItem
                                key={group.key}
                                active={active}
                                onClick={() => setActiveType(group.key)}
                            >
                                <div className="flex justify-between items-center w-full">
                                    <span>{group.title}</span>

                                </div>
                            </TypeItem>
                        );
                    })}
                </LeftPane>

                {/* RIGHT – Options */}
                <RightPane>
                    <div className="flex justify-between items-center mb-4">
                        <FilterTitle>
                            Select {activeGroup.title}
                        </FilterTitle>

                    </div>

                    <div className="space-y-1">
                        {activeGroup.options.map(option => {
                            const checked = isFilterSelected(activeGroup.key, option.value);

                            return (
                                <OptionRow
                                    key={option.value}
                                    onClick={() => toggleFilter(activeGroup.key, option)}
                                >
                                    <div className="flex items-center gap-3">
                                        <Checkbox checked={checked}>
                                            {checked && (
                                                <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M5 13l4 4L19 7" />
                                                </svg>
                                            )}
                                        </Checkbox>
                                        <span className={`leading-tight text-sm ${checked ? 'text-gray-800 font-medium' : 'text-gray-600'}`}>
                                            {option.label}
                                        </span>
                                    </div>
                                </OptionRow>
                            );
                        })}
                    </div>
                </RightPane>
            </Body>

            <ActionSection>
                <SideCancelBtn type="button" onClick={handleClearAllFilters}>
                    Clear All
                </SideCancelBtn>
                <SubmitBtn type="submit" onClick={handleApply}>
                    Apply Filters
                </SubmitBtn>
            </ActionSection>
        </Container>
    );
};

const Container = tw.div`bg-white w-full h-full flex flex-col`;
const ModalHeader = tw.div`flex items-center justify-between bg-primary-color px-4 py-4`;
const Body = tw.div`flex flex-1 overflow-hidden`;
const LeftPane = tw.div`w-1/3 bg-gray-50 border-r overflow-y-auto`;
const TypeItem = tw.div`px-4 py-4 cursor-pointer text-md ${({ active }) => active ? "bg-blue-50 text-primary-color font-medium border-l-4 border-primary-color" : "text-gray-700 hover:bg-gray-100"}`;
const RightPane = tw.div`flex-1 py-4 pl-4 pr-6 overflow-y-auto mb-16`;
const OptionRow = tw.div`flex items-center justify-between py-3 px-2 cursor-pointer rounded-md hover:bg-gray-50 transition-colors duration-150`;
const FilterTitle = tw.h3`text-md font-medium text-gray-700`;
const Checkbox = tw.div`
  w-4 h-4 border-2 rounded flex items-center justify-center
  transition-colors duration-150
  ${({ checked }) => checked
        ? 'bg-primary-color border-primary-color'
        : 'border-gray-300 bg-white hover:border-gray-400'}
`;

export default FilterModal;